"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Clock, FileText, Loader as Loader2, PhoneCall, Send, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface BenefitItem {
  id: number;
  title: string;
  description: string;
}
const benefits: BenefitItem[] = [{
  id: 1,
  title: "Expert Technical Appraisal",
  description: "Direct guidance from senior analytical instrumentation and qualification specialists."
}, {
  id: 2,
  title: "Rapid Technical Response",
  description: "Tailored quotes, feasibility assessments, and technical datasheets delivered swiftly."
}, {
  id: 3,
  title: "Regulatory Compliance Support",
  description: "Full alignment with GMP, GLP, and pharmacopeial standards across Ireland and the UK."
}];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [requestType, setRequestType] = useState<"quote" | "consultation">("quote");
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center p-8 bg-secondary/50 rounded-xl border border-border">
        <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Request Received</h3>
        <p className="text-muted-foreground max-w-md text-sm mb-6">
          {successMessage || "Thank you. An analytical specialist from Antech Solutions will review your requirements and respond promptly."}
        </p>
        <Button onClick={resetForm} variant="outline" size="sm">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex rounded-lg bg-muted p-1 border border-border/60">
        <Button type="button" variant="ghost" size="sm" onClick={() => setRequestType("quote")} className={`flex-1 text-xs font-medium h-8 rounded-md transition-all ${requestType === "quote" ? "bg-background text-foreground shadow-xs hover:bg-background" : "text-muted-foreground hover:text-foreground hover:bg-transparent"}`}>
          Request Quote
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setRequestType("consultation")} className={`flex-1 text-xs font-medium h-8 rounded-md transition-all ${requestType === "consultation" ? "bg-background text-foreground shadow-xs hover:bg-background" : "text-muted-foreground hover:text-foreground hover:bg-transparent"}`}>
          Book Consultation
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-foreground">Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="Dr. Sarah Murphy" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-foreground">Work Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="s.murphy@laboratory.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="organization" rules={{
        required: "Organization name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-foreground">Company / Facility *</FormLabel>
              <FormControl>
                <Input placeholder="BioPharma Lab Ireland" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="sector" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-foreground">Industry Sector</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value || "pharma"}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="pharma">Pharmaceutical Manufacturing</SelectItem>
                  <SelectItem value="biopharma">Biopharmaceutical</SelectItem>
                  <SelectItem value="academic">Academic & Research</SelectItem>
                  <SelectItem value="device">Medical Device</SelectItem>
                  <SelectItem value="chemical">Chemical Manufacturing</SelectItem>
                  <SelectItem value="other">Other Laboratory</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="notes" rules={{
      required: "Please describe your instrumentation or qualification requirement",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs text-foreground">Application Requirements *</FormLabel>
            <FormControl>
              <Textarea placeholder="Mention instrumentation scope, timeline, sample matrix, or qualification needs..." className="min-h-24 resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-xs text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-4" />
            Processing Request...
          </> : <>
            <Send className="mr-2 size-4" />
            {requestType === "quote" ? "Request Detailed Quote" : "Book Technical Consultation"}
          </>}
      </Button>

      <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground pt-1">
        <span className="flex items-center gap-1">
          <ShieldCheck className="size-3.5 text-primary" /> NDA Protected
        </span>
        <span className="flex items-center gap-1">
          <Clock className="size-3.5 text-primary" /> Guaranteed Fast Turnaround
        </span>
      </div>
    </Form>;
}
export default function ConsultationCTA() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Column: Copy, Highlights, Quick Contact */}
          <div className="lg:col-span-6 space-y-6">
            <Badge variant="secondary" className="px-3 py-1 gap-1.5 text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="size-3 text-primary" />
              Technical Partnership
            </Badge>

            <div className="space-y-3">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight">
                Accelerate Your Analytical Method & Equipment Selection
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                Get application-led advice, customized pricing, and qualification roadmaps for regulated laboratories and manufacturing teams across Ireland and the UK.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              {benefits.map((benefit, index) => <div key={benefit.id} data-index={index} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/40 border border-border/50">
                  <div className="mt-0.5 rounded-md bg-primary/10 p-1 text-primary shrink-0">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{benefit.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                      {benefit.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button asChild variant="outline" className="h-10 px-4 gap-2">
                <Link to="/ContactPage">
                  <PhoneCall className="size-4 text-primary" />
                  Speak to an Engineer
                </Link>
              </Button>
              <Button asChild variant="ghost" className="h-10 px-4 gap-2 text-muted-foreground hover:text-foreground">
                <Link to="/SolutionsPage">
                  <FileText className="size-4" />
                  Explore Solutions Portfolio
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Interactive Consultation Card */}
          <div className="lg:col-span-6">
            <Card className="shadow-lg border-border bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-card-foreground">
                  Request a Consultation or Quote
                </CardTitle>
                <CardDescription className="text-xs">
                  Fill in your project details below to receive expert technical guidance.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <FormProvider formId="consultation-quote-cta" sectionName="consultation-cta-section" defaultValues={{
                fullName: "",
                email: "",
                organization: "",
                sector: "pharma",
                notes: ""
              }} validationRules={{
                fullName: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                organization: {
                  required: true
                },
                notes: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Request Detailed Quote" successMessage="Thank you! An Antech Solutions technical specialist will be in touch within 24 business hours.">
                  <ConsultationFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>;
}